"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ProfileSummaryProps {
  handleSubmit: () => Promise<unknown>;
  isNewLanguageProfile: boolean;
}

export function ProfileSummary({ handleSubmit, isNewLanguageProfile }: ProfileSummaryProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation(["profile", "common"]);

  const onSubmit = async () => {
    setIsSubmitting(true);
    setHasError(false);
    const isError = await handleSubmit();

    if (!!isError) {
      setHasError(true);
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setHasError(false);
    onSubmit();
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
        delay: 0.2,
      },
    },
  };

  const errorVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
      },
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const normalVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      duration: 0.3,
      transition: {},
    },
  };

  return (
    <motion.div
      animate="visible"
      className="z-50 flex h-full w-full items-center justify-center px-3 py-6 max-sm:px-0 sm:px-4 sm:py-8"
      initial="hidden"
      variants={containerVariants}
    >
      <div className="max-h-[80dvh] w-full max-w-2xl overflow-hidden">
        <Card className="mx-auto w-full border border-none bg-transparent shadow-none!">
          <AnimatePresence mode="wait">
            {hasError ? (
              <motion.div
                key="error"
                animate="visible"
                className="flex flex-col items-center justify-center py-12 sm:py-16"
                exit="exit"
                initial="hidden"
                variants={errorVariants}
              >
                <div className="mb-6 flex items-center justify-center rounded-full border border-red-600/50 bg-red-400/30 p-4">
                  <Icon
                    className="text-red-500"
                    height="3rem"
                    icon="lucide:alert-circle"
                    width="3rem"
                  />
                </div>
                <h2 className="mb-2 text-xl font-bold sm:text-2xl">
                  {t("profile:profileSummary.error.title")}
                </h2>
                <p className="text-muted-foreground mb-6 max-w-md text-center text-sm sm:text-base">
                  {t("profile:profileSummary.error.description")}
                </p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => window.history.back()}>
                    {t("common:goBack")}
                  </Button>
                  <Button disabled={isSubmitting} variant="default" onClick={handleRetry}>
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        <span>{t("common:processing")}</span>
                      </div>
                    ) : (
                      <>
                        <Icon
                          className="mr-2"
                          height="1rem"
                          icon="lucide:refresh-cw"
                          width="1rem"
                        />
                        {t("common:retry")}
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="normal"
                animate="visible"
                exit="exit"
                initial="hidden"
                variants={normalVariants}
              >
                <CardHeader className="pb-4 text-center max-sm:px-0">
                  <motion.div className="mb-4 flex justify-center sm:mb-6" variants={iconVariants}>
                    <div className="bg-accent flex items-center rounded-full p-5">
                      <Icon
                        className="text-accent-foreground"
                        height="2.5rem"
                        icon="lucide:sparkles"
                        width="2.5rem"
                      />
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <CardTitle className="text-2xl font-bold sm:text-3xl">
                      {isNewLanguageProfile
                        ? t("profile:profileSummary.newLanguage.title")
                        : t("profile:profileSummary.firstProfile.title")}
                    </CardTitle>
                  </motion.div>
                  <motion.p
                    className="text-muted-foreground mt-2 text-sm sm:text-base"
                    variants={itemVariants}
                  >
                    {isNewLanguageProfile
                      ? t("profile:profileSummary.newLanguage.subtitle")
                      : t("profile:profileSummary.firstProfile.subtitle")}
                  </motion.p>
                </CardHeader>

                <ScrollArea className="mx-auto max-h-[50dvh] px-2">
                  <CardContent className="space-y-4 max-sm:px-0">
                    <motion.div className="rounded-lg p-2 sm:p-4" variants={itemVariants}>
                      <p className="text-center text-sm sm:text-base">
                        {isNewLanguageProfile
                          ? t("profile:profileSummary.newLanguage.description")
                          : t("profile:profileSummary.firstProfile.description")}
                      </p>
                    </motion.div>
                  </CardContent>
                </ScrollArea>

                <CardFooter className="flex flex-col items-center justify-center gap-4 pt-2 pb-6 max-sm:px-0 sm:pb-8">
                  <motion.div
                    className={"max-sm:w-full"}
                    variants={itemVariants}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Button
                      className="relative overflow-hidden px-6 py-5 text-base max-sm:w-full sm:px-8 sm:py-6 sm:text-lg"
                      disabled={isSubmitting}
                      size="lg"
                      variant="foreground"
                      onClick={onSubmit}
                    >
                      <AnimatePresence mode="wait">
                        {isSubmitting ? (
                          <motion.div
                            key="loading"
                            animate={{ opacity: 1 }}
                            className="flex items-center gap-2"
                            exit={{ opacity: 0 }}
                            initial={{ opacity: 0 }}
                          >
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            <span>{t("profile:profileSummary.creatingProfile")}</span>
                          </motion.div>
                        ) : (
                          <motion.span
                            key="text"
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            initial={{ opacity: 0 }}
                          >
                            {t("profile:profileSummary.createProfile")}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Button>
                  </motion.div>
                </CardFooter>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      </div>
    </motion.div>
  );
}
